import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import { toast } from 'react-toastify';
import couponService from '../../services/coupon.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const emptyCoupon = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 10,
  minOrderAmount: 0,
  maxDiscountAmount: '',
  usageLimit: '',
  perUserLimit: 1,
  endDate: '',
  isActive: true,
};

const FarmerCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyCoupon });
  const [saving, setSaving] = useState(false);

  const fetchCoupons = async () => {
    try {
      const { data } = await couponService.getMyCoupons();
      setCoupons(data.data);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.endDate) {
      toast.error('Code and expiry date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        maxDiscountAmount: form.maxDiscountAmount || null,
        usageLimit: form.usageLimit || null,
      };

      if (editingId) {
        await couponService.update(editingId, payload);
        toast.success('Coupon updated!');
      } else {
        await couponService.create(payload);
        toast.success('Coupon created!');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyCoupon });
      await fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount || 0,
      maxDiscountAmount: coupon.maxDiscountAmount || '',
      usageLimit: coupon.usageLimit || '',
      perUserLimit: coupon.perUserLimit || 1,
      endDate: coupon.endDate ? coupon.endDate.split('T')[0] : '',
      isActive: coupon.isActive,
    });
    setEditingId(coupon._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await couponService.delete(id);
      toast.success('Coupon deleted');
      setCoupons((prev) => prev.filter((c) => c._id !== id));
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Coupons</h2>
        <button
          className="btn btn-fd-primary"
          onClick={() => {
            setForm({ ...emptyCoupon });
            setEditingId(null);
            setShowForm(!showForm);
          }}
        >
          <FiPlus className="me-1" /> New Coupon
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="dashboard-card mb-4">
          <h5 className="mb-3">{editingId ? 'Edit Coupon' : 'Create Coupon'}</h5>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Coupon Code *</label>
                <input type="text" className="form-control text-uppercase" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SAVE20" required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount Type</label>
                <select className="form-select" value={form.discountType}
                  onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Discount Value {form.discountType === 'percentage' ? '(%)' : '($)'}
                </label>
                <input type="number" className="form-control" min="0" step="0.01" value={form.discountValue}
                  onChange={(e) => setForm({ ...form, discountValue: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Description</label>
                <input type="text" className="form-control" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Summer sale discount" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Expiry Date *</label>
                <input type="date" className="form-control" value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Min Order ($)</label>
                <input type="number" className="form-control" min="0" step="0.01" value={form.minOrderAmount}
                  onChange={(e) => setForm({ ...form, minOrderAmount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Max Discount ($)</label>
                <input type="number" className="form-control" min="0" step="0.01"
                  value={form.maxDiscountAmount} placeholder="No limit"
                  onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Usage Limit</label>
                <input type="number" className="form-control" min="0" placeholder="Unlimited"
                  value={form.usageLimit}
                  onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Per User Limit</label>
                <input type="number" className="form-control" min="1" value={form.perUserLimit}
                  onChange={(e) => setForm({ ...form, perUserLimit: parseInt(e.target.value) || 1 })} />
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })} id="couponActive" />
                  <label className="form-check-label" htmlFor="couponActive">Active</label>
                </div>
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button type="submit" className="btn btn-fd-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Coupon' : 'Create Coupon'}
              </button>
              <button type="button" className="btn btn-outline-secondary"
                onClick={() => { setShowForm(false); setEditingId(null); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Coupons List */}
      {coupons.length === 0 ? (
        <div className="dashboard-card text-center py-5">
          <FiTag size={40} className="text-muted mb-3" />
          <h5 className="text-muted">No coupons yet</h5>
          <p className="text-muted">Create your first coupon to attract more customers.</p>
        </div>
      ) : (
        <div className="dashboard-card p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Min Order</th>
                  <th>Usage</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => {
                  const isExpired = new Date(coupon.endDate) < new Date();
                  const isLimitReached = coupon.usageLimit && coupon.usedCount >= coupon.usageLimit;
                  return (
                    <tr key={coupon._id}>
                      <td>
                        <code className="fw-bold">{coupon.code}</code>
                        {coupon.description && (
                          <small className="d-block text-muted">{coupon.description}</small>
                        )}
                      </td>
                      <td>
                        {coupon.discountType === 'percentage'
                          ? `${coupon.discountValue}%`
                          : `$${coupon.discountValue.toFixed(2)}`}
                        {coupon.maxDiscountAmount && (
                          <small className="d-block text-muted">max ${coupon.maxDiscountAmount}</small>
                        )}
                      </td>
                      <td>{coupon.minOrderAmount > 0 ? `$${coupon.minOrderAmount}` : '—'}</td>
                      <td>
                        {coupon.usedCount}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                      </td>
                      <td>
                        <span className={isExpired ? 'text-danger' : ''}>
                          {new Date(coupon.endDate).toLocaleDateString()}
                        </span>
                      </td>
                      <td>
                        {isExpired ? (
                          <span className="badge bg-secondary">Expired</span>
                        ) : isLimitReached ? (
                          <span className="badge bg-warning text-dark">Limit Reached</span>
                        ) : coupon.isActive ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(coupon)}>
                            <FiEdit2 size={14} />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(coupon._id)}>
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerCouponsPage;
